// src/components/blocks/EdgeIndicator.jsx
import React from 'react';
import { Box, Tooltip } from '@mui/material';
import EDGE_WIDTH from '../../../../../../../constants/edgeWidth';
import { Edge } from '../../../../../../../types/shared/blocksV3';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import EdgeLabel from 'src/enums/edgeLabel';
import SegmentIndicator from './segmentIndicator';

const EdgeIndicator: React.FC<{
	edge: Edge;
	onSelectEdge: (label: EdgeLabel) => void;
	isSelected: boolean;
	parentIsSelected: boolean;
}> = ({ edge, onSelectEdge, isSelected, parentIsSelected }) => {
	const getEdgeStyles = () => {
		switch (edge.label) {
			case EdgeLabel.TOP:
				return {
					left: 0,
					right: 0,
					height: EDGE_WIDTH,
					display: 'flex',
					justifyContent: 'start',
				};
			case EdgeLabel.RIGHT:
				return {
					top: 0,
					bottom: 0,
					right: 0,
					width: EDGE_WIDTH,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'start',
				};
			case EdgeLabel.BOTTOM:
				return {
					left: 0,
					right: 0,
					bottom: 0,
					height: EDGE_WIDTH,
					display: 'flex',
					justifyContent: 'start',
				};
			case EdgeLabel.LEFT:
				return {
					top: 0,
					bottom: 0,
					width: EDGE_WIDTH,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'start',
				};
			default:
				return {};
		}
	};

	const getTooltipContent = () => {
		switch (edge.label.toLowerCase()) {
			case EdgeLabel.TOP:
				return `Top Edge: ${edge.label} - ${edge.length} inch(es)`;
			case EdgeLabel.RIGHT:
				return `Right Edge: ${edge.label} - ${edge.length} inch(es)`;
			case EdgeLabel.BOTTOM:
				return `Bottom Edge: ${edge.label} - ${edge.length} inch(es)`;
			case EdgeLabel.LEFT:
				return `Left Edge: ${edge.label} - ${edge.length} inch(es)`;
			default:
				return `Edge: ${edge.label} - ${edge.length} inch(es)`;
		}
	};

	const getTooltipPlacement = () => {
		switch (edge.label.toLowerCase()) {
			case EdgeLabel.TOP:
				return 'top';
			case EdgeLabel.RIGHT:
				return 'right';
			case EdgeLabel.BOTTOM:
				return 'bottom';
			case EdgeLabel.LEFT:
				return 'left';
			default:
				return 'top';
		}
	};

	const handleKeyDown = (e) => {
		if (e.key === 'Enter' || e.key === ' ') {
			onSelectEdge(edge.label);
		}
	};

	const pointerStyle = {
		color: 'white',
		position: 'absolute',
		top: 'calc(50% - 12px)',
		left: 'calc(50% - 12px)',
		zIndex: '50',
	};

	return (
		<Tooltip
			title={getTooltipContent()}
			arrow
			placement={getTooltipPlacement()}
		>
			<Box
				onClick={(e) => {
					e.stopPropagation();
					onSelectEdge(edge.label);
				}}
				onTouchStart={(e) => {
					e.stopPropagation();
				}}
				onTouchEnd={(e) => {
					e.stopPropagation();
					e.preventDefault();
					onSelectEdge(edge.label);
				}}
				onKeyDown={handleKeyDown}
				tabIndex={0} // Make focusable
				role="button" // Define role for accessibility
				aria-label={`Select ${edge.label} edge`}
				sx={{
					position: 'absolute',
					...getEdgeStyles(),
					cursor: 'pointer',
					touchAction: 'manipulation',
				}}
			>
				{parentIsSelected && edge.label == EdgeLabel.LEFT && (
					<ArrowLeftIcon sx={pointerStyle} />
				)}
				{parentIsSelected && edge.label == EdgeLabel.RIGHT && (
					<ArrowRightIcon sx={pointerStyle} />
				)}
				{parentIsSelected && edge.label == EdgeLabel.TOP && (
					<ArrowDropUpIcon sx={pointerStyle} />
				)}
				{parentIsSelected && edge.label == EdgeLabel.BOTTOM && (
					<ArrowDropDownIcon sx={pointerStyle} />
				)}

				{edge.segments?.map((segment, index) => (
					<SegmentIndicator
						key={index}
						index={index}
						segment={segment}
						isSelected={isSelected}
						edge={edge}
					/>
				))}
			</Box>
		</Tooltip>
	);
};

export default EdgeIndicator;
